import configurationOptionLabels from './configurationOptionLabels.json';

export type OrderProductType = 'suit' | 'shirt' | 'vest';

export interface PresentableOrderItem {
    id: string;
    productType: OrderProductType;
    configuration: Record<string, any>;
    measurements?: Record<string, any> | null;
    supplierSubtotal?: number;
    memberSubtotal?: number;
    supplierBreakdown?: unknown;
    memberBreakdown?: unknown;
}

export interface PresentableBreakdownLine {
    sectionKey?: string;
    itemId?: string;
    label?: string;
    amount: number;
    kind?: string;
}

export interface PresentableMeasurementSection {
    label: string;
    rows: Array<[string, string]>;
}

const MEASUREMENT_LABELS: Record<string, string> = {
    selectedSize: 'Taille', longueurDevant: 'Longueur devant', longueurDos: 'Longueur du dos',
    tourPoitrine: 'Tour de poitrine', tourVentre: 'Tour de ventre', tourHanches: 'Tour de hanches',
    largeurEpaules: 'Largeur épaules', largeurHautDos: 'Largeur haut du dos',
    longueurManches: 'Longueur manches', tourBiceps: 'Tour de biceps', tourPoignet: 'Tour de poignet',
    tourDeTaille: 'Tour de taille', bassin: 'Bassin',
    montant: 'Montant', cuisse: 'Cuisse',
    longueurExtGauche: 'Longueur extérieure gauche',
    longueurExtDroite: 'Longueur extérieure droite', basFini: 'Bas fini',
};

const normalizeGarmentSection = (section: 'veste' | 'gilet', data: Record<string, any>): Record<string, any> => ({
    selectedSize: data.selectedSize ?? null,
    longueurDevant: data.longueurDevant ?? null,
    longueurDos: data.longueurDos ?? null,
    tourPoitrine: data.tourPoitrine ?? data.poitrine ?? null,
    tourVentre: data.tourVentre ?? data.estomac ?? null,
    tourHanches: data.tourHanches ?? data.bassin ?? null,
    largeurEpaules: data.largeurEpaules ?? data.epaules ?? null,
    ...(section === 'veste' ? {
        largeurHautDos: data.largeurHautDos ?? null,
        longueurManches: data.longueurManches ?? data.mancheGauche ?? data.mancheDroite ?? null,
        tourBiceps: data.tourBiceps ?? data.biceps ?? null,
        tourPoignet: data.tourPoignet ?? null,
    } : {}),
});

export interface OrderListPresentation {
    itemCount: number;
    productTypes: OrderProductType[];
    itemSummaries: Array<{
        id: string;
        productType: OrderProductType;
        label: string;
        fabric: string | null;
        supplierSubtotal: number | null;
        memberSubtotal: number | null;
    }>;
}

const PRODUCT_TYPES = new Set<OrderProductType>(['suit', 'shirt', 'vest']);

export const PRODUCT_TYPE_LABELS: Record<OrderProductType, string> = {
    suit: 'Costume',
    shirt: 'Chemise',
    vest: 'Gilet',
};

export const CONFIGURATION_LABELS: Record<string, string> = {
    tissuId: 'Référence tissu',
    liasseNamespace: 'Liasse',
    suitPiece: 'Type de costume',
    jacketCut: 'Coupe de la veste',
    colType: 'Type de col',
    shirtColor: 'Référence tissu chemise',
    chemise: 'Modèle de chemise',
    basDeVeste: 'Bas de veste',
    vestType: 'Type de gilet',
    vestFabricChoice: 'Choix du tissu gilet',
    vestFabric: 'Référence tissu gilet',
    vestBackFabric: 'Dos du gilet',
    vestLiasseNamespace: 'Liasse gilet',
    pantsFabricChoice: 'Choix du tissu pantalon',
    pantsFabric: 'Référence tissu pantalon',
    pantsLiasseNamespace: 'Liasse pantalon',
    vestSize: 'Taille gilet',
    revers: 'Revers',
    collarSize: 'Largeur du col',
    pochettePoitrine: 'Poche poitrine',
    teteDeManche: 'Tête de manche',
    styleDePoche: 'Poches basses',
    button: 'Boutons',
    surpiqure: 'Surpiqûre',
    surpiqureColor: 'Couleur surpiqûre',
    boutonniereDeCol: 'Boutonnière de col',
    boutonniereDeColColor: 'Couleur boutonnière de col',
    fenteArriere: 'Fentes arrière',
    ouvertureDeManche: 'Ouverture de manche',
    boutonDeManche: 'Boutons de manche',
    boutonDeMancheColor: 'Couleur boutons de manche',
    boutonDeManchePosition: 'Position boutonnière de manche',
    jacketButtonholeColor: 'Couleur boutonnière veste',
    positionContrasteVeste: 'Position de contraste de veste',
    jacketContrastColor: 'Couleur de contraste veste',
    construction: 'Construction',
    interiorStyle: 'Doublure',
    interiorStyleColor: 'Référence de doublure de veste',
    jacketLining: 'Référence de doublure de veste',
    collarFelt: 'Feutre de col',
    collarFeltReference: 'Référence feutre de col',
    pantalon: 'Pantalon',
    pantalonHauteurTaille: 'Hauteur de taille',
    basDePantalon: 'Bas de pantalon',
    reversSize: 'Dimension du revers',
    doublurePantalon: 'Doublure du pantalon',
    pantsLiningColor: 'Référence de doublure du pantalon',
    pantsLining: 'Référence de doublure du pantalon',
    pantsButton: 'Bouton du pantalon',
    pochetteCoin: 'Pochette de coin',
    pocheArriere: 'Poche arrière',
    positionContrastePantalon: 'Position de contraste du pantalon',
    pantsContrastColor: 'Couleur de contraste pantalon',
    pocheDeGilet: 'Poches de gilet',
    vestButton: 'Boutons du gilet',
    reversGilet: 'Revers de gilet',
    vestCollarSize: 'Largeur du col gilet',
    arriereGilet: 'Arrière de gilet',
    positionContrasteGilet: 'Position de contraste de gilet',
    vestContrastColor: 'Couleur de contraste gilet',
    vestButtonholeColor: 'Couleur boutonnière gilet',
    embroideryEnabled: 'Broderie personnalisée',
    embroideryTarget: 'Pièce brodée',
    embroideryText: 'Texte broderie',
    embroideryThreadColor: 'Couleur broderie',
    embroideryJacketPosition: 'Position broderie veste',
    embroideryVestPosition: 'Position broderie gilet',
    vestEmbroideryText: 'Texte broderie gilet',
    vestEmbroideryThreadColor: 'Couleur broderie gilet',
    vestEmbroiderySize: 'Taille broderie gilet',
    vestEmbroideryFont: 'Police broderie gilet',
    embroiderySize: 'Taille broderie',
    embroideryFont: 'Police broderie',
    pantsEmbroideryText: 'Texte broderie pantalon',
    pantsEmbroideryThreadColor: 'Couleur broderie pantalon',
    embroideryPantsPosition: 'Position broderie pantalon',
    pantsEmbroiderySize: 'Taille broderie pantalon',
    pantsEmbroideryFont: 'Police broderie pantalon',
    useSameEmbroiderySettings: 'Réglages de broderie identiques',
};

const CONFIGURATION_ORDER = Object.keys(CONFIGURATION_LABELS);
const INTERNAL_CONFIGURATION_KEYS = new Set([
    'ethnicity',
    'selectedEthnicity',
    '__productType',
    'v',
    'acceptedTerms',
    'deferPayment',
    'dreamBox',
    'measurements',
    'pricingSnapshot',
    'clientPricingSnapshot',
    'supplierBreakdown',
    'memberBreakdown',
    'supplierSubtotal',
    'memberSubtotal',
    'clientSubtotal',
]);

const PRODUCT_EXCLUDED_KNOWN_KEYS: Record<OrderProductType, Set<string>> = {
    suit: new Set(['chemise', 'shirtColor']),
    shirt: new Set(CONFIGURATION_ORDER.filter((key) => ![
        'chemise', 'shirtColor', 'tissuId', 'liasseNamespace',
        'embroideryEnabled', 'embroideryText', 'embroideryThreadColor',
        'embroiderySize', 'embroideryFont',
    ].includes(key))),
    vest: new Set(CONFIGURATION_ORDER.filter((key) => ![
        'tissuId', 'liasseNamespace', 'vestType', 'vestFabricChoice',
        'vestFabric', 'vestBackFabric', 'vestLiasseNamespace', 'vestSize', 'vestButton', 'pocheDeGilet',
        'reversGilet', 'vestCollarSize', 'arriereGilet', 'positionContrasteGilet', 'vestContrastColor',
        'vestButtonholeColor', 'button', 'embroideryEnabled', 'embroideryTarget',
        'embroideryText', 'embroideryThreadColor', 'embroideryVestPosition',
        'embroiderySize', 'embroideryFont',
    ].includes(key))),
};

const isRecord = (value: unknown): value is Record<string, any> =>
    typeof value === 'object' && value !== null && !Array.isArray(value);

const ITEM_METADATA_KEYS = new Set([
    'id', 'itemId', 'lineId', 'productType', 'type', 'product',
    'configuration', 'config', 'snapshot', 'data', 'measurements',
    'sourceDraftId', 'quantity', 'price', 'subtotal', 'total',
    'supplierSubtotal', 'memberSubtotal', 'clientSubtotal',
    'supplierBreakdown', 'memberBreakdown', 'clientBreakdown',
    'createdAt', 'updatedAt', 'label',
]);

const PRODUCT_TYPE_ALIASES: Record<string, OrderProductType> = {
    suit: 'suit',
    costume: 'suit',
    complet: 'suit',
    shirt: 'shirt',
    chemise: 'shirt',
    vest: 'vest',
    gilet: 'vest',
};

const SNAPSHOT_FIELD_ALIASES: Record<string, string> = {
    selectedChemiseColor: 'shirtColor',
    collarFeltReference: 'collarFeltReference',
    embroideryText: 'embroideryText',
    embroideryEnabled: 'embroideryEnabled',
    embroideryTarget: 'embroideryTarget',
    useSameEmbroiderySettings: 'useSameEmbroiderySettings',
    pantsEmbroideryText: 'pantsEmbroideryText',
};

const normalizeConfiguration = (source: Record<string, any>): Record<string, any> => {
    const configuration: Record<string, any> = {};
    for (const [key, value] of Object.entries(source)) {
        if (ITEM_METADATA_KEYS.has(key) || INTERNAL_CONFIGURATION_KEYS.has(key)) continue;
        if (key.startsWith('selected') && key.length > 'selected'.length) {
            const suffix = key.slice('selected'.length);
            const normalizedKey = SNAPSHOT_FIELD_ALIASES[key]
                ?? `${suffix.charAt(0).toLowerCase()}${suffix.slice(1)}`;
            if (configuration[normalizedKey] === undefined) configuration[normalizedKey] = value;
            continue;
        }
        configuration[SNAPSHOT_FIELD_ALIASES[key] ?? key] = value;
    }
    return configuration;
};

const getRawItemConfiguration = (rawItem: Record<string, any>): Record<string, any> => {
    const nestedData = isRecord(rawItem.data) ? rawItem.data : null;
    // Certaines commandes de transition ont stocké les choix directement sur
    // la ligne du panier. On fusionne toutes les formes connues, la structure
    // canonique `configuration` restant prioritaire lorsqu'une clé existe à
    // plusieurs endroits.
    const configuration = normalizeConfiguration(rawItem);
    [
        nestedData,
        nestedData?.configuration,
        rawItem.snapshot,
        rawItem.config,
        rawItem.configuration,
    ].forEach((source) => {
        if (isRecord(source)) Object.assign(configuration, normalizeConfiguration(source));
    });
    return configuration;
};

const normalizeProductType = (
    rawItem: Record<string, any>,
    configuration: Record<string, any>,
): OrderProductType | null => {
    const rawType = rawItem.productType
        ?? rawItem.type
        ?? rawItem.product
        ?? rawItem.configuration?.__productType
        ?? rawItem.snapshot?.__productType;
    const alias = typeof rawType === 'string' ? PRODUCT_TYPE_ALIASES[rawType.toLowerCase()] : undefined;
    if (alias) return alias;
    if (configuration.suitPiece || configuration.jacketCut || configuration.colType) return 'suit';
    if (configuration.chemise || configuration.shirtColor) return 'shirt';
    if (configuration.vestType || configuration.vestSize) return 'vest';
    return null;
};

const finiteNumber = (value: unknown): number | undefined => {
    const number = typeof value === 'number' ? value : Number(value);
    return Number.isFinite(number) ? number : undefined;
};

export const isOrderV2 = (order: Record<string, any>): boolean =>
    Array.isArray(order.items)
    && (Number(order.schemaVersion) === 2 || order.items.some((item: unknown) => isRecord(item)));

/**
 * Normalise uniquement la structure de lecture. Les éléments invalides sont
 * ignorés afin qu'une ancienne commande ou une écriture Firestore partielle ne
 * puisse pas casser une liste, un e-mail ou un PDF.
 */
export const getOrderItems = (order: Record<string, any>): PresentableOrderItem[] => {
    if (!isOrderV2(order)) return [];

    return order.items.flatMap((rawItem: unknown, index: number) => {
        if (!isRecord(rawItem)) return [];
        const configuration = getRawItemConfiguration(rawItem);
        const productType = normalizeProductType(rawItem, configuration);
        if (!productType || !PRODUCT_TYPES.has(productType)) return [];
        const supplierSubtotal = finiteNumber(rawItem.supplierSubtotal);
        // `clientSubtotal` a existé dans le panier local avant que le nom métier
        // `memberSubtotal` soit figé : on le lit encore, sans l'écrire.
        const memberSubtotal = finiteNumber(rawItem.memberSubtotal ?? rawItem.clientSubtotal);

        return [{
            id: [rawItem.id, rawItem.itemId, rawItem.lineId]
                .find((value) => typeof value === 'string' && value.trim()) || `item-${index + 1}`,
            productType,
            configuration,
            measurements: [rawItem.measurements, rawItem.configuration?.measurements, rawItem.snapshot?.measurements]
                .find(isRecord) ?? null,
            supplierSubtotal,
            memberSubtotal,
            supplierBreakdown: rawItem.supplierBreakdown,
            memberBreakdown: rawItem.memberBreakdown ?? rawItem.clientBreakdown,
        }];
    });
};

export const humanizeIdentifier = (value: string): string => value
    .replace(/^bxf_/, '')
    .replace(/([a-zà-ÿ0-9])([A-Z])/g, '$1 $2')
    .replace(/[_-]+/g, ' ')
    .replace(/\s+/g, ' ')
    .trim();

const RAW_REFERENCE_KEYS = new Set([
    'tissuId', 'liasseNamespace', 'shirtColor', 'vestFabric', 'vestBackFabric',
    'vestLiasseNamespace', 'pantsFabric', 'pantsLiasseNamespace',
    'interiorStyleColor', 'jacketLining', 'pantsLiningColor', 'pantsLining',
    'collarFeltReference', 'button', 'vestButton', 'pantsButton',
    'jacketContrastColor', 'pantsContrastColor', 'vestContrastColor',
    'surpiqureColor', 'boutonniereDeColColor', 'boutonDeMancheColor',
    'jacketButtonholeColor', 'vestButtonholeColor',
    'embroideryThreadColor', 'pantsEmbroideryThreadColor', 'vestEmbroideryThreadColor',
]);

const EMBROIDERY_SIZE_KEYS = new Set(['embroiderySize', 'pantsEmbroiderySize', 'vestEmbroiderySize']);

export const formatConfigurationValue = (key: string, value: unknown): string | null => {
    if (value === null || value === undefined || value === '') return null;
    if (typeof value === 'boolean') return value ? 'Oui' : 'Non';
    if (typeof value === 'number') return String(value);
    if (Array.isArray(value)) {
        const values = value.map((entry) => formatConfigurationValue(key, entry)).filter((entry): entry is string => !!entry);
        return values.length > 0 ? values.join(', ') : null;
    }
    if (isRecord(value)) return null;

    const raw = String(value);
    const optionLabel = (configurationOptionLabels as Record<string, Record<string, string>>)[key]?.[raw];
    if (optionLabel) return optionLabel;
    if (key === 'suitPiece') return raw === '3_pieces' ? '3 pièces' : raw === '2_pieces' ? '2 pièces' : humanizeIdentifier(raw);
    if (key === 'collarSize' || key === 'vestCollarSize' || key === 'pantalonHauteurTaille' || key === 'reversSize') {
        return /cm$/i.test(raw) ? raw : `${raw} cm`;
    }
    if (EMBROIDERY_SIZE_KEYS.has(key) && /^\d+(?:[.,]\d+)?$/.test(raw)) return `${raw} mm`;
    if (raw === 'match' && ['interiorStyleColor', 'jacketLining', 'pantsLiningColor', 'pantsLining'].includes(key)) {
        return 'Assortie au costume';
    }
    if (raw === 'match' && EMBROIDERY_SIZE_KEYS.has(key)) return 'Match';
    if (raw === 'match') return 'Assorti au tissu';
    if (raw === 'other') return 'Autre';
    if (raw === 'none' || raw === 'sans' || raw === 'aucun') return 'Sans';
    if (['liasseNamespace', 'vestLiasseNamespace', 'pantsLiasseNamespace'].includes(key)) {
        const name = raw.replace(/^liasse_/, '').replace(/_/g, ' ');
        return `Liasse ${name === 'sm1' ? 'SM1' : name === 'nobility58' ? 'Nobility58' : name.toUpperCase()}`;
    }
    if (RAW_REFERENCE_KEYS.has(key)) return raw;
    return humanizeIdentifier(raw);
};

const getNestedConfigurationRows = (
    key: string,
    rowLabel: string,
    value: unknown,
): Array<[string, string]> => {
    if (Array.isArray(value)) {
        return value.flatMap((entry, index) => getNestedConfigurationRows(key, `${rowLabel} ${index + 1}`, entry));
    }
    if (!isRecord(value)) {
        const formatted = formatConfigurationValue(key, value);
        return formatted ? [[rowLabel, formatted]] : [];
    }

    return Object.entries(value).flatMap(([childKey, childValue]) =>
        getNestedConfigurationRows(
            childKey,
            `${rowLabel} - ${CONFIGURATION_LABELS[childKey] || humanizeIdentifier(childKey)}`,
            childValue,
        ));
};

const isApplicableConfigurationKey = (item: PresentableOrderItem, key: string): boolean => {
    const c = item.configuration;
    if (INTERNAL_CONFIGURATION_KEYS.has(key) || PRODUCT_EXCLUDED_KNOWN_KEYS[item.productType].has(key)) return false;
    // La liasse principale sert à retrouver le tissu et son tarif. Sa ligne
    // seule répète une valeur par défaut (souvent ROSE) dans les récapitulatifs.
    if (key === 'liasseNamespace') return false;
    if (item.productType === 'suit' && c.suitPiece !== '3_pieces' && [
        'vestType', 'vestFabricChoice', 'vestFabric', 'vestBackFabric', 'vestLiasseNamespace', 'vestSize',
        'vestButton', 'vestCollarSize', 'pocheDeGilet', 'reversGilet', 'arriereGilet',
        'positionContrasteGilet', 'vestContrastColor', 'vestButtonholeColor',
    ].includes(key)) return false;
    if (key === 'collarFeltReference' && c.collarFelt !== 'other') return false;
    if (key === 'vestCollarSize' && (!c.reversGilet || c.reversGilet === 'none')) return false;
    if ((key === 'pantsFabric' || key === 'pantsLiasseNamespace') && c.pantsFabricChoice === 'same') return false;
    if ((key === 'vestFabric' || key === 'vestLiasseNamespace') && c.vestFabricChoice === 'same') return false;
    if (key === 'pantsLiasseNamespace' && !c.pantsFabric) return false;
    if (key === 'vestLiasseNamespace' && !c.vestFabric) return false;
    if (key === 'reversSize' && c.basDePantalon !== 'revers') return false;
    if (key === 'interiorStyleColor' && (!c.interiorStyle || c.interiorStyle === 'none')) return false;
    if (key === 'surpiqureColor' && (!c.surpiqure || c.surpiqure === 'none')) return false;
    if (key === 'boutonniereDeColColor' && (!c.boutonniereDeCol || c.boutonniereDeCol === 'none')) return false;
    if (key === 'boutonDeMancheColor' && (!c.boutonDeManche || c.boutonDeManche === 'none')) return false;
    if (key === 'pantsLiningColor' && c.doublurePantalon !== 'avec') return false;
    if (key === 'jacketContrastColor' && !c.positionContrasteVeste?.length) return false;
    if (key === 'pantsContrastColor' && !c.positionContrastePantalon?.length) return false;
    if (key === 'vestContrastColor' && !c.positionContrasteGilet?.length) return false;
    if (key === 'boutonDeManchePosition' && (!c.boutonDeManche || c.boutonDeManche === 'none')) return false;
    if (key.startsWith('embroidery') || key.startsWith('pantsEmbroidery') || key.startsWith('vestEmbroidery') || key === 'useSameEmbroiderySettings') {
        if (key === 'embroideryEnabled') return true;
        const hasHistoricEmbroidery = c.embroideryEnabled == null && Boolean(
            c.embroideryText || c.pantsEmbroideryText || c.vestEmbroideryText
            || (c.embroideryJacketPosition && c.embroideryJacketPosition !== 'none')
            || (c.embroideryVestPosition && c.embroideryVestPosition !== 'none')
            || (c.embroideryPantsPosition && c.embroideryPantsPosition !== 'none'));
        if (c.embroideryEnabled !== true && !hasHistoricEmbroidery) return false;
        if (item.productType === 'suit') {
            const target = c.embroideryTarget;
            const includesJacket = !target || ['jacket', 'both', 'jacket_vest', 'all'].includes(target);
            const includesPants = !target || ['pants', 'both', 'pants_vest', 'all'].includes(target);
            const includesVest = ['vest', 'jacket_vest', 'pants_vest', 'all'].includes(target);
            if (key === 'embroideryVestPosition' && !includesVest) return false;
            if (key.startsWith('vestEmbroidery') && !includesVest) return false;
            if (key === 'embroideryJacketPosition' && !includesJacket) return false;
            if (key === 'embroideryPantsPosition' && !includesPants) return false;
            if (key.startsWith('pantsEmbroidery') && !includesPants) return false;
            if (key === 'useSameEmbroiderySettings' && !(includesPants && (includesJacket || includesVest))) return false;
            if (!includesJacket && [
                'embroideryText', 'embroideryThreadColor', 'embroiderySize', 'embroideryFont',
            ].includes(key)) return false;
        }
    }
    return true;
};

export const getConfigurationRows = (
    item: PresentableOrderItem,
): Array<[string, string]> => {
    const configuration = { ...item.configuration };
    if (item.productType === 'suit' && ['vest', 'jacket_vest', 'pants_vest', 'all'].includes(configuration.embroideryTarget)) {
        if (configuration.vestEmbroideryText === undefined) configuration.vestEmbroideryText = configuration.embroideryText;
        if (configuration.vestEmbroideryThreadColor === undefined) configuration.vestEmbroideryThreadColor = configuration.embroideryThreadColor;
        if (configuration.vestEmbroiderySize === undefined) configuration.vestEmbroiderySize = configuration.embroiderySize;
        if (configuration.vestEmbroideryFont === undefined) configuration.vestEmbroideryFont = configuration.embroideryFont;
    }
    if (item.productType === 'suit' && configuration.embroideryEnabled === true
        && ['both', 'pants_vest', 'all'].includes(configuration.embroideryTarget) && configuration.useSameEmbroiderySettings === true) {
        const source = configuration.embroideryTarget === 'pants_vest' ? 'vest' : 'jacket';
        configuration.pantsEmbroideryText = source === 'vest' ? configuration.vestEmbroideryText : configuration.embroideryText;
        configuration.pantsEmbroideryThreadColor = source === 'vest' ? configuration.vestEmbroideryThreadColor : configuration.embroideryThreadColor;
        configuration.pantsEmbroiderySize = source === 'vest' ? configuration.vestEmbroiderySize : configuration.embroiderySize;
        configuration.pantsEmbroideryFont = source === 'vest' ? configuration.vestEmbroideryFont : configuration.embroideryFont;
    }
    const knownKeys = CONFIGURATION_ORDER.filter((key) => key in configuration);
    const unknownKeys = Object.keys(configuration)
        .filter((key) => !CONFIGURATION_LABELS[key] && !INTERNAL_CONFIGURATION_KEYS.has(key))
        .sort((a, b) => a.localeCompare(b, 'fr'));
    const keys = [...knownKeys, ...unknownKeys];

    return keys.flatMap((key) => {
        if (!isApplicableConfigurationKey(item, key)) return [];
        const baseLabel = CONFIGURATION_LABELS[key] || humanizeIdentifier(key);
        const rowLabel = item.productType === 'suit' && [
            'embroideryText', 'embroideryThreadColor', 'embroiderySize', 'embroideryFont',
        ].includes(key) ? `${baseLabel} veste` : baseLabel;
        const rows = getNestedConfigurationRows(key, rowLabel, configuration[key]);
        if (key === 'boutonDeManche' && typeof configuration[key] === 'string') {
            const match = /^(\d+)(?:_(chevauchee))?_(droit|en_biais)$/.exec(configuration[key]);
            if (match) {
                rows.push([`${rowLabel} - Nombre`, match[1]]);
                rows.push([`${rowLabel} - Disposition`, match[2] ? 'Chevauchés' : 'Alignés']);
                rows.push([`${rowLabel} - Orientation`, match[3] === 'droit' ? 'Droits' : 'En biais']);
            }
        }
        return rows;
    });
};

export interface PresentableConfigurationSection {
    label: string;
    rows: Array<[string, string]>;
}

const JACKET_KEYS = new Set([
    'jacketCut', 'colType', 'basDeVeste', 'revers', 'collarSize', 'pochettePoitrine',
    'teteDeManche', 'styleDePoche', 'button', 'surpiqure', 'surpiqureColor',
    'boutonniereDeCol', 'boutonniereDeColColor', 'fenteArriere', 'ouvertureDeManche',
    'boutonDeManche', 'boutonDeMancheColor', 'boutonDeManchePosition',
    'jacketButtonholeColor', 'positionContrasteVeste', 'jacketContrastColor',
    'construction', 'interiorStyle', 'interiorStyleColor', 'jacketLining',
    'collarFelt', 'collarFeltReference',
]);
const PANTS_KEYS = new Set([
    'pantsFabricChoice', 'pantsFabric', 'pantsLiasseNamespace', 'pantalon',
    'pantalonHauteurTaille', 'basDePantalon', 'reversSize', 'doublurePantalon',
    'pantsLiningColor', 'pantsLining', 'pantsButton', 'pochetteCoin', 'pocheArriere',
    'positionContrastePantalon', 'pantsContrastColor', 'pantsEmbroideryText',
    'pantsEmbroideryThreadColor', 'pantsEmbroiderySize', 'pantsEmbroideryFont',
]);
const VEST_KEYS = new Set([
    'vestType', 'vestFabricChoice', 'vestFabric', 'vestBackFabric',
    'vestLiasseNamespace', 'vestSize', 'pocheDeGilet', 'vestButton', 'reversGilet',
    'vestCollarSize', 'arriereGilet', 'positionContrasteGilet',
    'vestContrastColor', 'vestButtonholeColor',
]);
const EMBROIDERY_KEYS = new Set([
    'embroideryEnabled', 'embroideryTarget', 'embroideryText',
    'embroideryThreadColor', 'embroiderySize', 'embroideryFont',
    'useSameEmbroiderySettings', 'embroideryJacketPosition',
    'embroideryVestPosition', 'embroideryPantsPosition',
    'vestEmbroideryText', 'vestEmbroideryThreadColor', 'vestEmbroiderySize', 'vestEmbroideryFont',
]);

/** Même découpage utilisé par les récapitulatifs, les e-mails et les PDF. */
export const getConfigurationSections = (item: PresentableOrderItem): PresentableConfigurationSection[] => {
    const rows = getConfigurationRows(item);
    const sections: PresentableConfigurationSection[] = [
        { label: 'Tissu et composition', rows: [] },
        ...(item.productType === 'shirt' ? [{ label: 'Chemise', rows: [] as Array<[string, string]> }] : []),
        ...(item.productType === 'suit' ? [
            { label: 'Veste', rows: [] as Array<[string, string]> },
            { label: 'Pantalon', rows: [] as Array<[string, string]> },
        ] : []),
        ...(item.productType === 'vest' || item.configuration.suitPiece === '3_pieces'
            ? [{ label: 'Gilet', rows: [] as Array<[string, string]> }] : []),
        { label: 'Broderie', rows: [] },
        { label: 'Autres détails', rows: [] },
    ];
    const rowSection = (label: string): string => {
        const key = [...CONFIGURATION_ORDER]
            .sort((a, b) => CONFIGURATION_LABELS[b].length - CONFIGURATION_LABELS[a].length)
            .find((candidate) => label === CONFIGURATION_LABELS[candidate]
                || label.startsWith(`${CONFIGURATION_LABELS[candidate]} `)
                || label.startsWith(`${CONFIGURATION_LABELS[candidate]} - `));
        if (!key) return 'Autres détails';
        if (PANTS_KEYS.has(key)) return 'Pantalon';
        if (VEST_KEYS.has(key)) return 'Gilet';
        if (JACKET_KEYS.has(key)) return 'Veste';
        if (EMBROIDERY_KEYS.has(key)) return 'Broderie';
        if (key === 'chemise' || key === 'shirtColor') return 'Chemise';
        return 'Tissu et composition';
    };
    for (const row of rows) {
        const label = rowSection(row[0]);
        (sections.find((section) => section.label === label) || sections[sections.length - 1]).rows.push(row);
    }
    return sections.filter((section) => section.rows.length > 0);
};

export const getLegacyOrderItem = (order: Record<string, any>): PresentableOrderItem => {
    const nested = isRecord(order.configuration) ? order.configuration : {};
    const configuration = Object.fromEntries(CONFIGURATION_ORDER.flatMap((key) => {
        const value = key in nested ? nested[key] : order[key];
        return value === undefined ? [] : [[key, value]];
    }));
    return {
        id: String(order.id ?? 'legacy-order'),
        productType: 'suit',
        configuration,
        measurements: isRecord(order.measurements) ? order.measurements : null,
    };
};

export const getMeasurementSections = (item: PresentableOrderItem): PresentableMeasurementSection[] => {
    if (!isRecord(item.measurements)) return [];

    const sections: PresentableMeasurementSection[] = [];
    const rootRows: Array<[string, string]> = [];
    for (const [sectionKey, rawSection] of Object.entries(item.measurements)) {
        if (!isRecord(rawSection)) {
            if (rawSection !== null && rawSection !== undefined && rawSection !== '') {
                rootRows.push([
                    MEASUREMENT_LABELS[sectionKey] || humanizeIdentifier(sectionKey),
                    sectionKey === 'selectedSize' || typeof rawSection !== 'number'
                        ? String(rawSection)
                        : `${rawSection} cm`,
                ]);
            }
            continue;
        }

        const displayedSection = sectionKey === 'veste' || sectionKey === 'gilet'
            ? normalizeGarmentSection(sectionKey, rawSection) : rawSection;
        const rows = Object.entries(displayedSection).flatMap(([key, value]) => {
            if (value === null || value === undefined || value === '') return [];
            const measurementLabel = MEASUREMENT_LABELS[key] || humanizeIdentifier(key);
            const measurementValue = key === 'selectedSize' || typeof value !== 'number'
                ? String(value)
                : `${value} cm`;
            return [[measurementLabel, measurementValue] as [string, string]];
        });
        if (rows.length > 0) {
            sections.push({ label: humanizeIdentifier(sectionKey), rows });
        }
    }
    if (rootRows.length > 0) sections.unshift({ label: 'Mesures', rows: rootRows });
    return sections;
};

export const getItemFabricLabel = (item: PresentableOrderItem): string | null => {
    const configuration = item.configuration;
    const fabric = configuration.tissuId
        ?? (item.productType === 'vest' ? configuration.vestFabric : undefined)
        ?? configuration.fabricId
        ?? configuration.fabric;
    if (!fabric) return null;
    const namespace = configuration.liasseNamespace
        ?? (item.productType === 'vest' ? configuration.vestLiasseNamespace : undefined)
        ?? configuration.fabricNamespace;
    return `${String(fabric)}${namespace ? ` (${String(namespace)})` : ''}`;
};

export const getItemSummaryLabel = (item: PresentableOrderItem): string => {
    const configuration = item.configuration;
    const parts: string[] = [];
    if (item.productType === 'suit' && configuration.suitPiece) {
        const type = formatConfigurationValue('suitPiece', configuration.suitPiece);
        if (type) parts.push(type);
    }
    const fabric = getItemFabricLabel(item);
    if (fabric) parts.push(`tissu ${fabric}`);
    return parts.length > 0 ? parts.join(' — ') : PRODUCT_TYPE_LABELS[item.productType];
};

export const getOrderListPresentation = (order: Record<string, any>): OrderListPresentation | null => {
    const items = getOrderItems(order);
    if (!isOrderV2(order)) return null;
    return {
        itemCount: items.length,
        productTypes: Array.from(new Set(items.map((item) => item.productType))),
        itemSummaries: items.map((item, index) => ({
            id: item.id,
            productType: item.productType,
            label: `Article ${index + 1} — ${PRODUCT_TYPE_LABELS[item.productType]}`,
            fabric: getItemFabricLabel(item),
            supplierSubtotal: item.supplierSubtotal ?? null,
            memberSubtotal: item.memberSubtotal ?? null,
        })),
    };
};

export const getBreakdownLines = (breakdown: unknown): PresentableBreakdownLine[] => {
    const rawLines = Array.isArray(breakdown)
        ? breakdown
        : isRecord(breakdown) && Array.isArray(breakdown.lines)
            ? breakdown.lines
            : [];

    return rawLines.flatMap((rawLine: unknown) => {
        if (!isRecord(rawLine)) return [];
        const amount = finiteNumber(rawLine.amount);
        if (amount === undefined) return [];
        return [{
            sectionKey: typeof rawLine.sectionKey === 'string' ? rawLine.sectionKey : undefined,
            itemId: typeof rawLine.itemId === 'string' ? rawLine.itemId : undefined,
            label: typeof rawLine.label === 'string' ? rawLine.label : undefined,
            kind: typeof rawLine.kind === 'string' ? rawLine.kind : undefined,
            amount,
        }];
    });
};

export const sumItemSubtotals = (
    items: PresentableOrderItem[],
    audience: 'supplier' | 'client',
): number => items.reduce((total, item) => total + (audience === 'supplier'
    ? item.supplierSubtotal ?? 0
    : item.memberSubtotal ?? 0), 0);
